import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, vdjLoop, vdjCameraKick, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Le pavage est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'truchet',
  family: 'shader',
  danceability: 0.86,
  requiresTier: 0,
  energyLevel: 0.82,
  bpmMin: 115,
  bpmMax: 175,
  movement: 0.85,
  tonality: 0.45,
  prefers: { gridStrength: [0.65, 1.0] },
  enabled: true
};

// Truchet : un pavage infini de tuiles carrées, chacune portant deux quarts
// de cercle qui relient les milieux de ses côtés. Raccordés, les arcs
// forment des chemins néon continus qui serpentent en labyrinthe. LOOK VDJ
// party (lib/vdjLook.js).
//
// UN SEUL QUAD PLEIN ÉCRAN, en pleine résolution, identique sur tous les
// tiers. Pour chaque pixel :
//   p   la position en unités de hauteur, centrée sur la pochette
//   pc  la caméra : roulis (un tour en 64 temps + à-coup de mesure), zoom d'à-coup
//   pf  le kaléidoscope (même pli que VDJ_MIRROR_CHUNK, mais centré sur la
//       pochette et sans le repli des coins)
//   X   la déformation de la phrase (FLAT, FISHEYE, TWIRL, POLAR, WARP)
//   q   les coordonnées de tuile d'un niveau : X x N x 2^(L - z)
//
// LA LARGEUR EN PIXELS (R1) : la distance à l'arc d est en unités de tuile ;
// sa pente à l'écran est |J^T n|, où n est la normale de l'arc dans le
// repère de tuile et J la jacobienne pixel -> tuile, mesurée par différences
// finies sur trois évaluations de la déformation (en polaire, la différence
// d'angle est repliée sur la couture). dPx = d / |J^T n| : les coeurs font
// 2 px dans toutes les déformations et à toutes les échelles. Le pli du
// kaléidoscope est une isométrie : la jacobienne prise au point plié suffit.
//
// LES ARCS : la distance au QUART d'arc exact (pas au cercle entier), sur la
// tuile du pixel et sa voisine de l'autre côté du bord le plus proche. Une
// tuile qui tourne garde ses arcs dans son disque inscrit, donc dans sa
// case ; la voisine du bord le plus proche suffit pour que le halo ne soit
// jamais coupé net là où les arcs se raccordent (aux milieux des côtés).
//
// L'ÉTAT D'UNE TUILE, sans texture : bit = hachage(tuile, graine du niveau)
// XOR (nombre de bascules achevées mod 2). Les vagues finies sont résumées
// par trois compteurs en forme fermée :
//   - RING / SWEEP / SPIRAL / ALL : chaque tuile bascule une fois par vague
//   - CHECKER n : les tuiles de parité n mod 2 -> floor((C + 1 - parité) / 2)
//   - RANDOM n : les tuiles de case s = floor(7 h) == n mod 7 (14 % chacune)
//     -> floor((R + 6 - s) / 7)
// Les vagues encore en propagation (six au plus) passent en uniformes : âge
// en temps, mode, indice. Une tuile tourne de 90° en un huitième de temps
// (ease-out) ; tourner les arcs de 90° échange les deux orientations.
//
// LES COULEURS QUI COULENT : chaque arc relie un côté vertical à un côté
// horizontal. Parcouru vertical -> horizontal dans les tuiles paires,
// horizontal -> vertical dans les impaires, le sens est cohérent le long de
// tout chemin ; l'abscisse s = (t + parité) / 2 est continue d'une tuile à
// l'autre (un cycle de palette pour deux arcs). Le décalage avance d'un cran
// par temps ; son sens change à chaque mesure.
//
// LE ZOOM INFINI : deux niveaux, le second deux fois plus fin. z va de 0 à
// 1 en 8 temps (facteur 2) ; à z = 1 le niveau fin a exactement la grille du
// grossier à z = 0, et sa graine devient celle du grossier (génération g) :
// le relais est exact, sans couture. Poids : voir levelWeights.
// En POLAIRE (log-polaire, M tuiles par tour), le zoom est une translation
// radiale : un seul niveau, et l'indice de tuile est périodique (angle mod M,
// rayon mod 256).

const N_TILES = 8;               // tuiles par hauteur, niveau grossier, z = 0
const POLAR_M = 18;              // tuiles par tour en polaire (pair : parité continue à la couture)
const ZOOM_BEATS = 8;            // un facteur 2
const ROLL_BEATS = 64;
const FLIP_BEATS = 1 / 8;
const WAVE_SPAN = 1;             // un front de vague traverse l'écran en un temps
const WAVE_SLOTS = 6;
const FLOW_STEP = 0.125;         // le cran de couleur par temps (un quart d'arc)
const DROP_BARS = 4;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const SAFE_FLOOR = 0.5;
const EXPOSURE = 1.0;
const LUM_PERIOD_S = 0.5;
const LUM_W = 64, LUM_H = 36;

export const WAVES = ['ring', 'sweep', 'checker', 'random', 'spiral'];
const WAVE_ALL = 5;
export const WARPS = ['flat', 'fisheye', 'twirl', 'polar', 'warp'];
const WARP_POLAR = 3;
// la durée max d'une vague (délai max + rotation), par mode
const WAVE_MAX_DELAY = [WAVE_SPAN, WAVE_SPAN, 0, 0.5, WAVE_SPAN, 0];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

const fragmentShader = `
  precision highp float;
  uniform float uAspect;
  uniform float uPx;             // la taille d'un pixel, en unités de hauteur
  uniform float uRoll;
  uniform float uKickZoom;
  uniform float uWedges;         // 0 : pas de kaléidoscope ; 4 ou 6
  uniform float uWarp;           // 0..4
  uniform float uTwirl;
  uniform vec2 uWarpT;           // les phases du WARP
  uniform float uZ;              // la phase du zoom, 0..1
  uniform vec2 uSeed;            // graines des niveaux grossier et fin
  uniform vec2 uWeight;          // poids des deux niveaux
  uniform float uShift;          // translation radiale du polaire (mod 256)
  uniform vec3 uCount;           // vagues finies : (toutes mod 2, damier mod 4, hasard mod 14)
  uniform vec4 uWave[${WAVE_SLOTS}];   // âge (temps), mode, indice, vivante
  uniform float uFlow;
  uniform vec3 uHue;            // les trois teintes de la palette
  uniform float uExposure;
  uniform float uFlash;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  uniform vec2 uStageCenter;     // R19 : le centre de la composition
  varying vec2 vUv;

  const float PI = 3.14159265;
  const float TAU = 6.28318531;
  const float M = ${POLAR_M.toFixed(1)};

  float hash(vec2 c, float s) {
    vec3 p = fract(vec3(c.xyx + s * 17.31) * 0.1031);
    p += dot(p, p.yzx + 33.33);
    return fract((p.x + p.y) * p.z);
  }

  vec2 rot(vec2 v, float a) { float c = cos(a), s = sin(a); return vec2(c * v.x - s * v.y, s * v.x + c * v.y); }

  vec2 fold(vec2 p, float wedges) {
    float r = length(p);
    float w = TAU / wedges;
    float a = mod(atan(p.y, p.x), 2.0 * w);
    a = abs(a - w);
    return vec2(cos(a), sin(a)) * r;
  }

  // la déformation : p (unités de hauteur, centré sur la pochette) -> X
  vec2 warp(vec2 p) {
    float r = length(p);
    if (uWarp > 2.5 && uWarp < 3.5) {
      // POLAIRE : log-polaire conforme, M tuiles par tour
      return vec2(atan(p.y, p.x), log(max(r, 1e-4))) * (M / TAU);
    }
    if (uWarp > 0.5 && uWarp < 1.5) {
      // FISHEYE : bombement autour de la pochette (monotone : 1 - 0,55 > 0)
      p *= 1.0 - 0.55 * exp(-r * r / 0.16);
    } else if (uWarp > 3.5) {
      // WARP : amplitude 0,03 < la moitié d'une tuile fine (R14), A f = 0,24
      p += 0.03 * vec2(sin(p.y * 8.0 + uWarpT.x), sin(p.x * 8.0 + uWarpT.y));
    }
    // TWIRL : torsion qui augmente vers le centre (un pas de rotation : det = 1)
    if (uTwirl > 0.0) p = rot(p, uTwirl * exp(-r / 0.30));
    return p;
  }

  // le délai d'une tuile dans une vague, en temps
  float waveDelay(float mode, vec2 P, float h) {
    if (mode < 0.5) return ${WAVE_SPAN.toFixed(1)} * clamp(length(P), 0.0, 1.0);
    if (mode < 1.5) return ${WAVE_SPAN.toFixed(1)} * clamp((P.x + P.y) / 2.8 + 0.5, 0.0, 1.0);
    if (mode < 2.5) return 0.0;
    if (mode < 3.5) return 0.5 * h;
    if (mode < 4.5) return ${WAVE_SPAN.toFixed(1)} * fract(atan(P.y, P.x) / TAU + length(P) * 1.2);
    return 0.0;
  }

  // l'état d'une tuile : (bit, rotation 0..1)
  vec2 tileState(vec2 c, float seed, vec2 P, float parity) {
    float h0 = hash(c, seed);
    float h1 = hash(c + 71.0, seed);
    float slot = floor(h1 * 7.0);
    float n = uCount.x + floor((uCount.y + 1.0 - parity) * 0.5) + floor((uCount.z + 6.0 - slot) / 7.0);
    float turn = 0.0;
    for (int k = 0; k < ${WAVE_SLOTS}; k++) {
      vec4 w = uWave[k];
      if (w.w < 0.5) continue;
      float sel = 1.0;
      if (w.y > 1.5 && w.y < 2.5) sel = abs(mod(w.z, 2.0) - parity) < 0.5 ? 1.0 : 0.0;
      else if (w.y > 2.5 && w.y < 3.5) sel = abs(mod(w.z, 7.0) - slot) < 0.5 ? 1.0 : 0.0;
      if (sel < 0.5) continue;
      float ph = (w.x - waveDelay(w.y, P, h1)) / ${FLIP_BEATS.toFixed(4)};
      if (ph >= 1.0) n += 1.0;
      else if (ph > 0.0) { float e = 1.0 - ph; turn += 1.0 - e * e * e; }
    }
    float bit = mod(floor(h0 * 2.0) + n, 2.0);
    return vec2(bit, turn);
  }

  // la palette cosinus des trois teintes de la pochette, cyclique. On
  // interpole la TEINTE (moyenne circulaire pondérée par les cosinus), pas le
  // RVB : un mélange RVB de deux teintes opposées passe par le gris. Saturation
  // pleine, canal max 1, luminance plafonnée à 0,7 (gaine R1).
  vec3 palette(float s) {
    vec3 w = 0.5 + 0.5 * cos(TAU * (s - vec3(0.0, 1.0, 2.0) / 3.0));
    w *= w * w;
    vec2 v = w.x * vec2(cos(TAU * uHue.x), sin(TAU * uHue.x))
           + w.y * vec2(cos(TAU * uHue.y), sin(TAU * uHue.y))
           + w.z * vec2(cos(TAU * uHue.z), sin(TAU * uHue.z));
    float h = atan(v.y, v.x) / TAU;
    vec3 c = clamp(abs(mod(h * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
    float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
    return c * min(1.0, 0.7 / max(lum, 1e-3));
  }

  // un quart d'arc autour du coin k (dans le repère local de la tuile) :
  // (d, t, n.x, n.y) - t va du côté vertical (0) au côté horizontal (1)
  vec4 quarter(vec2 u, vec2 k) {
    vec2 sg = vec2(k.x < 0.5 ? 1.0 : -1.0, k.y < 0.5 ? 1.0 : -1.0);
    vec2 v = (u - k) * sg;
    if (v.x >= 0.0 && v.y >= 0.0) {
      float l = length(v);
      vec2 n = (l > 1e-5 ? v / l : vec2(0.7071)) * sign(l - 0.5 + 1e-6);
      return vec4(abs(l - 0.5), atan(v.x, v.y) / (0.5 * PI), n * sg);
    }
    vec2 eV = v - vec2(0.0, 0.5), eH = v - vec2(0.5, 0.0);
    float dV = length(eV), dH = length(eH);
    if (dV < dH) return vec4(dV, 0.0, eV / max(dV, 1e-5) * sg);
    return vec4(dH, 1.0, eH / max(dH, 1e-5) * sg);
  }

  // une tuile : (dPx, puis la couleur)
  vec4 tile(vec2 q, vec2 c, float seed, vec2 dqx, vec2 dqy, float scale, float period, bool polar) {
    vec2 cc = c;
    if (polar) cc = vec2(mod(c.x, period), mod(c.y, 256.0));
    float parity = mod(cc.x + cc.y, 2.0);
    // le centre de la tuile dans le plan (unités de hauteur), pour les délais
    vec2 P;
    if (polar) {
      float a = (c.x + 0.5) / scale * (TAU / M);
      float lr = ((c.y + 0.5) / scale + uShift) * (TAU / M);
      P = exp(lr) * vec2(cos(a), sin(a));
    } else P = (c + 0.5) / scale;
    vec2 st = tileState(cc, seed, P, parity);
    float ang = st.y * 0.5 * PI;
    vec2 u = rot(q - c - 0.5, -ang) + 0.5;
    vec2 k0 = st.x < 0.5 ? vec2(0.0) : vec2(1.0, 0.0);
    vec2 k1 = st.x < 0.5 ? vec2(1.0) : vec2(0.0, 1.0);
    vec4 a0 = quarter(u, k0), a1 = quarter(u, k1);
    // la pente de d à l'écran : |J^T n|, n ramené dans le repère du plan
    vec2 n0 = rot(a0.zw, ang), n1 = rot(a1.zw, ang);
    float g0 = max(length(vec2(dot(n0, dqx), dot(n0, dqy))), 1e-5);
    float g1 = max(length(vec2(dot(n1, dqx), dot(n1, dqy))), 1e-5);
    float p0 = a0.x / g0, p1 = a1.x / g1;
    float t = p0 < p1 ? a0.y : a1.y;
    float dPx = min(p0, p1);
    float tf = parity < 0.5 ? t : 1.0 - t;
    vec3 col = palette((tf + parity) * 0.5 + uFlow);
    // pendant le quart de tour, le sens de parcours s'inverse : on passe à
    // la couleur de l'orientation d'arrivée
    if (st.y > 0.0) col = mix(col, palette((1.0 - tf + parity) * 0.5 + uFlow), clamp(st.y, 0.0, 1.0));
    return vec4(dPx, col);
  }

  vec4 level(vec2 X, vec2 dX, vec2 dY, float L, float seed, bool polar) {
    float scale, period = 1.0;
    vec2 q;
    if (polar) {
      scale = exp2(L);
      period = M * scale;
      q = X * scale - vec2(0.0, uShift * scale);
    } else {
      scale = ${N_TILES.toFixed(1)} * exp2(L - uZ);
      q = X * scale;
    }
    vec2 dqx = dX * scale, dqy = dY * scale;
    // la taille d'une tuile en pixels : en dessous, les arcs se confondent
    float tilePx = inversesqrt(max(abs(dqx.x * dqy.y - dqx.y * dqy.x), 1e-12));
    float lod = smoothstep(9.0, 20.0, tilePx);
    if (lod <= 0.0) return vec4(0.0);
    vec2 c = floor(q);
    vec2 f = q - c;
    // la voisine de l'autre côté du bord le plus proche
    vec2 e = abs(f.x - 0.5) > abs(f.y - 0.5) ? vec2(f.x < 0.5 ? -1.0 : 1.0, 0.0) : vec2(0.0, f.y < 0.5 ? -1.0 : 1.0);
    vec4 a = tile(q, c, seed, dqx, dqy, scale, period, polar);
    vec4 b = tile(q, c + e, seed, dqx, dqy, scale, period, polar);
    vec4 best = a.x < b.x ? a : b;
    // le profil R1 : coeur blanc chaud 2 px, gaine saturée 6 px, halo exp(-d/10) x 0,35.
    // Les valeurs de R1 sont des valeurs AFFICHÉES : le halo passe en linéaire
    // (puissance 2,2), sinon ses 0,08 linéaires s'affichent en voile gris à 80/255
    float dPx = best.x;
    float core = 1.0 - smoothstep(0.6, 1.4, dPx);
    float sheath = 1.0 - smoothstep(2.4, 3.6, dPx);
    float halo = pow(0.35 * exp(-dPx / 10.0), 2.2);
    vec3 col = best.yzw * (sheath + (1.0 - sheath) * halo);
    col = mix(col, vec3(1.25, 1.18, 1.05), core);
    return vec4(col * lod, 0.0);
  }

  float safeAtt(vec2 uv) {
    vec2 s = (uv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  void main() {
    // p : unités de hauteur (la hauteur vaut 1), centré sur la scène (R19)
    vec2 p = 0.5 * ((vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uStageCenter);
    vec2 pc = rot(p, uRoll) / uKickZoom;
    float h = uPx / uKickZoom;
    if (uWedges > 0.5) pc = fold(pc, uWedges);
    vec2 X = warp(pc);
    vec2 dX = warp(pc + vec2(h, 0.0)) - X;
    vec2 dY = warp(pc + vec2(0.0, h)) - X;
    bool polar = uWarp > 2.5 && uWarp < 3.5;
    if (polar) {
      // la couture de l'angle : une différence d'un tour se replie
      dX.x -= M * floor(dX.x / M + 0.5);
      dY.x -= M * floor(dY.x / M + 0.5);
    }
    vec3 col = vec3(0.0);
    if (uWeight.x > 0.01) col += uWeight.x * level(X, dX, dY, 0.0, uSeed.x, polar).rgb;
    if (uWeight.y > 0.01) col += uWeight.y * level(X, dX, dY, 1.0, uSeed.y, polar).rgb;
    col = col * uExposure + vec3(uFlash);
    gl_FragColor = vec4(col * safeAtt(vUv), 1.0);
  }
`;

let MAT = null;
function material() {
  if (MAT) return MAT;
  const waves = [];
  for (let i = 0; i < WAVE_SLOTS; i++) waves.push(new THREE.Vector4(0, 0, 0, 0));
  MAT = new THREE.ShaderMaterial({
    uniforms: {
      uAspect: safeZoneUniforms.uAspect, uPx: { value: 1 / 720 },
      uRoll: { value: 0 }, uKickZoom: { value: 1 }, uWedges: { value: 0 }, uWarp: { value: 0 }, uTwirl: { value: 0 },
      uWarpT: { value: new THREE.Vector2() }, uZ: { value: 0 }, uSeed: { value: new THREE.Vector2(0, 1) },
      uWeight: { value: new THREE.Vector2(1, 0) }, uShift: { value: 0 }, uCount: { value: new THREE.Vector3() },
      uWave: { value: waves }, uFlow: { value: 0 },
      uHue: { value: new THREE.Vector3(0.88, 0.5, 0.12) },
      uExposure: { value: EXPOSURE }, uFlash: { value: 0 },
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather,
      uStageCenter: safeZoneUniforms.uStageCenter
    },
    vertexShader: quadVertexShader, fragmentShader, depthTest: false, depthWrite: false
  });
  return MAT;
}

// les poids des deux niveaux à la phase z du zoom. Fondus réglés en valeurs
// AFFICHÉES (puis passés en linéaire, puissance 2,2) et de pente nulle aux
// deux bouts : à z = 0 le niveau fin naît à 0, à z = 1 le grossier s'éteint
// à 0 - le relais n'a ni saut ni coude. (En cos / sin linéaires, le sRGB
// affichait déjà 20 % du niveau naissant à z = 0,017 : un « pop » à chaque
// relais.) La densité affichée respire de 1 à 1,45 sur la boucle de 8 temps.
export function levelWeights(z) {
  const ss = (a, b, x) => { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); };
  return [Math.pow(1 - ss(0.2, 1.0, z), 2.2), Math.pow(ss(0.0, 0.8, z), 2.2)];
}
function hueOf(hex) { const hsl = { h: 0, s: 0, l: 0 }; new THREE.Color(hex).getHSL(hsl); return hsl.h; }
function hueDist(a, b) { const d = Math.abs(a - b) % 1; return Math.min(d, 1 - d); }

export class TruchetVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    const wv = WAVES.indexOf(urlParam('truchetwave'));
    this._forceWave = wv >= 0 ? wv : null;
    const wp = WARPS.indexOf(urlParam('truchetwarp'));
    this._forceWarp = wp >= 0 ? wp : null;
    this._oneLevel = urlParam('truchetlevel') === '1';
    this._noWaves = urlParam('truchetnowave') === '1';
    const mi = parseInt(urlParam('truchetmirror'), 10);
    this._forceMirror = Number.isFinite(mi) ? mi : null;
    // le look VDJ party : pas de tone mapping (le néon reste saturé, les
    // noirs restent noirs), l'aberration de la passe filmic pulsée au temps.
    // PAS DE BLOOM : sur un cadre couvert de traits fins, les grands flous du
    // bloom partagé font une moyenne uniforme - à 0,12 déjà, le fond passe de
    // 1 à 30/255 (mesuré). Le halo R1 est dessiné par le shader lui-même.
    const bs = parseFloat(urlParam('truchetbloom'));
    this._post = new VdjPost({ bloomStrength: Number.isFinite(bs) ? bs : 0, bloomThreshold: 0.78, grain: 0.012, vignette: 0.45, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    this._mat = material(); this._u = this._mat.uniforms;
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._mat);
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._beforeRender(renderer); };
    scene.add(this._display);
    // la mesure de confort : le même shader rendu en 64 x 36, avec la taille
    // de pixel du plein cadre (les traits y couvrent la même fraction d'écran)
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_W, LUM_H, { depthBuffer: false, stencilBuffer: false });
    this._lumPixels = new Uint8Array(LUM_W * LUM_H * 4);
    this._lumScene = new THREE.Scene();
    this._lumQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._mat);
    this._lumQuad.frustumCulled = false;
    this._lumScene.add(this._lumQuad);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._lumAge = 0; this._lumMeasured = 0.15; this._lumPending = false;

    // l'état musical
    this._time = 0;
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null; this._free = 0;
    this._beats = 0; this._bars = 0; this._phrases = 0; this._secondsBeat = 0.5;
    this._clock = 0;                      // l'horloge en temps, monotone
    this._prevPh = null;                  // la phase du temps à l'image précédente
    this._zoomAcc = 0;                    // zoom cumulé, en facteurs 2
    this._flow = 0; this._flowDir = 1;
    this._waves = []; this._allCount = 0; this._chkEmit = 0; this._chkDone = 0; this._rndEmit = 0; this._rndDone = 0;
    this._waveMode = 0; this._warpMode = 0; this._mirror = 0; this._palette = 0;
    this._buildup = 0; this._dropUntil = -1; this._flash = 0;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._paletteSeen = undefined; this._paletteHex = null;
    this._applyPalette();
  }

  _applyPalette(profile) {
    const pal = profile && profile.artworkPalette;
    if (profile) {
      if (pal === this._paletteSeen && this._paletteHex) return;
      this._paletteSeen = pal;
    }
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors.slice(0, 3) : [0xff2bd6, 0x2bffea, 0xffc21a];
    // trois teintes de la pochette, écartées d'au moins 0,25 (R4) : une
    // teinte trop proche est remplacée par la triade de la première
    const h0 = hueOf(v[0]);
    const hexes = [v[0]];
    for (let k = 1; k < 3; k++) {
      const cand = v[k];
      const ok = cand != null && hexes.every((hx) => hueDist(hueOf(hx), hueOf(cand)) >= 0.25);
      hexes.push(ok ? cand : new THREE.Color().setHSL((h0 + k / 3) % 1, 1, 0.5).getHex());
    }
    this._paletteHex = hexes;
    this._writePalette();
  }

  // la palette de la phrase : les trois teintes tournées d'un huitième de tour par phrase
  _writePalette() {
    if (!this._paletteHex) return;
    const shift = (this._palette % 8) * 0.125;
    // les teintes de vdjColor (la teinte de la pochette, saturation imposée)
    const h = this._paletteHex.map((hx) => hueOf(vdjColor(hx).getHex()));
    this._u.uHue.value.set((h[0] + shift) % 1, (h[1] + shift) % 1, (h[2] + shift) % 1);
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  _emitWave(mode) {
    if (this._noWaves) return;
    let index = 0;
    if (mode === 2) index = this._chkEmit++;
    else if (mode === 3) index = this._rndEmit++;
    // plus de place : la plus ancienne est close de force (ses tuiles finissent d'un coup)
    if (this._waves.length >= WAVE_SLOTS) this._retire(this._waves.shift());
    this._waves.push({ t0: this._clock, mode, index });
  }

  _retire(w) {
    if (w.mode === 2) this._chkDone++;
    else if (w.mode === 3) this._rndDone++;
    else this._allCount++;
  }

  onTrackChange(profile) { this._applyPalette(profile); this._dropIdx = 0; this._lastNowMs = null; }

  update(deltaTime, profile) {
    if (profile.playbackState !== 'PLAYING') return;      // pause : gel
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    this._applyPalette(profile);
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / THREE.MathUtils.clamp(bpm, 60, 200) : 0.5;
    const barS = 4 * this._secondsBeat;

    let beat = false, bar = false, phrase = false;
    const bp = profile.beatPhase;
    if (bp != null) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; }
    else { this._free += dt; if (this._free >= this._secondsBeat) { this._free -= this._secondsBeat; beat = true; } }
    const barPhase = profile.barPhase;
    if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; }
    else if (beat && (this._beats + 1) % 4 === 0) bar = true;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
    if (beat) this._beats++;
    if (bar) this._bars++;
    const beatPh = bp != null ? bp : this._free / this._secondsBeat;
    const beatCont = this._beats + beatPh;
    // l'horloge des vagues : monotone, en temps
    this._clock += dt / this._secondsBeat;

    // la montée : un drop connu qui approche, ou le segment « build »
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._buildup += (build - this._buildup) * (1 - Math.exp(-dt / 1.0));

    // PHRASE : mode de vague, déformation, palette suivants ; kaléidoscope une phrase sur deux
    if (phrase) {
      this._phrases++;
      this._waveMode = (this._waveMode + 1) % WAVES.length;
      this._warpMode = (this._warpMode + 1) % WARPS.length;
      this._palette++;
      this._writePalette();
      this._mirror = this._phrases % 2 === 1 ? (this._phrases % 4 === 1 ? 4 : 6) : 0;
    }

    // DROP : toutes les tuiles basculent ensemble, flash, POLAIRE + kaléidoscope 6 pendant 4 mesures
    if (this._tickDrops(profile)) {
      this._dropUntil = this._time + DROP_BARS * barS;
      this._emitWave(WAVE_ALL);
      if (this._comfort.requestFlash(this._time)) {
        if (director) director.flash = VDJ_DROP_FLASH; else this._flash = VDJ_DROP_FLASH;
      }
    }
    const inDrop = this._time < this._dropUntil;

    // TEMPS : une vague (à la croche puis à la double croche en montée), un cran de couleur
    const rate = this._buildup > 0.66 ? 4 : this._buildup > 0.33 ? 2 : 1;
    const mode = this._forceWave != null ? this._forceWave : this._waveMode;
    if (beat) { this._flow += FLOW_STEP * this._flowDir; this._emitWave(mode); }
    else if (rate > 1 && this._prevPh != null) {
      // les subdivisions franchies depuis l'image précédente (croches, doubles croches)
      for (let k = 1; k < rate; k++) { const e = k / rate; if (this._prevPh < e && beatPh >= e) this._emitWave(mode); }
    }
    this._prevPh = beatPh;
    // MESURE : à-coup de caméra, le défilement change de sens
    if (bar) this._flowDir = -this._flowDir;
    const kick = vdjCameraKick(barPhase != null ? barPhase : (this._beats % 4 + beatPh) / 4, this._bars, 4);

    // les vagues finies passent dans les compteurs
    while (this._waves.length) {
      let done = -1;
      for (let i = 0; i < this._waves.length; i++) {
        const w = this._waves[i];
        if (this._clock - w.t0 > WAVE_MAX_DELAY[w.mode] + FLIP_BEATS + 0.02) { done = i; break; }
      }
      if (done < 0) break;
      this._retire(this._waves.splice(done, 1)[0]);
    }

    // le zoom : un facteur 2 en 8 temps, qui accélère en montée
    this._zoomAcc += dt / this._secondsBeat / ZOOM_BEATS * (1 + 2 * this._buildup);
    const gen = Math.floor(this._zoomAcc), z = this._zoomAcc - gen;

    const warp = this._forceWarp != null ? this._forceWarp : (inDrop ? WARP_POLAR : this._warpMode);
    const polar = warp === WARP_POLAR;
    const U = this._u;
    U.uRoll.value = vdjLoop(beatCont, ROLL_BEATS) * Math.PI * 2 + kick.roll;
    U.uKickZoom.value = kick.zoom;
    U.uWedges.value = this._forceMirror != null ? this._forceMirror : (inDrop ? 6 : this._mirror);
    U.uWarp.value = warp;
    // TWIRL : torsion propre au mode, et qui se resserre en montée sur toutes les déformations planes
    U.uTwirl.value = polar ? 0 : (warp === 2 ? 2.2 : 0) + 3.0 * this._buildup;
    U.uWarpT.value.set(vdjLoop(beatCont, 16) * Math.PI * 2, vdjLoop(beatCont + 5, 16) * Math.PI * 2);
    U.uZ.value = z;
    U.uSeed.value.set(gen % 256, (gen + 1) % 256);
    if (polar || this._oneLevel) U.uWeight.value.set(1, 0);
    else { const w = levelWeights(z); U.uWeight.value.set(w[0], w[1]); }
    U.uShift.value = (this._zoomAcc * Math.LN2 * POLAR_M / (2 * Math.PI)) % 256;
    U.uCount.value.set(this._allCount % 2, this._chkDone % 4, this._rndDone % 14);
    for (let i = 0; i < WAVE_SLOTS; i++) {
      const w = this._waves[i], v = U.uWave.value[i];
      if (w) v.set(this._clock - w.t0, w.mode, w.index, 1); else v.set(0, -1, 0, 0);
    }
    U.uFlow.value = this._flow % 64;
    // le flash de repli (sans directeur) : une image à 0,85, puis plus rien
    U.uFlash.value = this._flash; this._flash = 0;

    // le confort : la luminance moyenne mesurée, après le gain courant
    this._lumAge += dt;
    if (this._lumAge >= LUM_PERIOD_S) { this._lumAge = 0; this._lumPending = true; }
    U.uExposure.value = EXPOSURE * this._comfort.measure(this._lumMeasured, dt);
    // la séparation RGB sur le temps
    this._post.update(vdjBeatChroma(beatPh, this._secondsBeat));
  }

  _beforeRender(renderer) {
    const t = renderer.getRenderTarget();
    let h;
    if (t) h = t.height;
    else { const v = renderer.getDrawingBufferSize(this._bufSize || (this._bufSize = new THREE.Vector2())); h = v.y; }
    this._u.uPx.value = 1 / Math.max(1, h);
    if (this._lumPending) {
      this._lumPending = false;
      renderer.setRenderTarget(this._lumTarget);
      renderer.render(this._lumScene, this._ortho);
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_W, LUM_H, this._lumPixels);
      renderer.setRenderTarget(t);
      let sum = 0;
      const px = this._lumPixels;
      for (let i = 0; i < px.length; i += 4) {
        const l = (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]) / 255;
        sum += Math.pow(l, 1 / 2.2);
      }
      this._lumMeasured = sum / (LUM_W * LUM_H);
    }
  }

  get probe() {
    return {
      clock: this._clock, zoom: this._zoomAcc, wave: WAVES[this._waveMode] || 'all', warp: WARPS[this._u.uWarp.value],
      wedges: this._u.uWedges.value, waves: this._waves.map((w) => ({ age: this._clock - w.t0, mode: w.mode, index: w.index })),
      counts: [this._allCount, this._chkDone, this._rndDone], flow: this._flow, buildup: this._buildup,
      exposure: this._u.uExposure.value, lum: this._lumMeasured, beats: this._beats, bars: this._bars, phrases: this._phrases
    };
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); this._display.geometry.dispose(); }
    if (this._lumQuad) this._lumQuad.geometry.dispose();
    if (this._lumTarget) { this._lumTarget.dispose(); this._lumTarget = null; }
    this._scene = null;
  }
}
